import { useState } from 'react';
import { useIntl } from '@leagueplatform/locales';
import {
  VisuallyHidden,
  StackLayout,
  Modal,
  Button,
} from '@leagueplatform/genesis-core';
import { type DropdownSection } from '@leagueplatform/web-common-components';
import { type NavLink, LinkType } from 'hooks/use-nav-links-list.hook';
import {
  MobileNavLink,
  MobileNavLandingPageLink,
} from 'components/header-nav/mobile-nav/mobile-nav-link.component';

type MobileNavProps = {
  navLinks: NavLink[];
  userDropdownConfig: DropdownSection[];
};
const MOBILE_NAV_MENU_WIDTH = 327;

export const MobileNav = ({ navLinks, userDropdownConfig }: MobileNavProps) => {
  const { formatMessage } = useIntl();
  const [isOpen, setIsOpen] = useState(false);

  const closeMenu = () => setIsOpen(false);

  return (
    <Modal.Root open={isOpen} onOpenChange={setIsOpen}>
      <Modal.Trigger>
        <Button
          icon="interfaceMenu"
          hideLabel
          quiet
          priority="tertiary"
          size="large"
          css={{
            marginInline: '$half',
            marginBlock: '$quarter',
          }}
        >
          {formatMessage({ id: 'OPEN_MENU' })}
        </Button>
      </Modal.Trigger>
      <Modal.Content
        layout="right"
        css={{
          '.GDS-modal': {
            maxWidth: MOBILE_NAV_MENU_WIDTH,
            backgroundColor: '$surfaceBackgroundSecondary',
            borderRadius: '$none',
          },
          '.GDS-modal-content': { paddingInlineStart: '$two' },
          '.GDS-modal-content ul': {
            listStyle: 'none',
            padding: 0,
          },
        }}
      >
        <StackLayout as="nav" spacing="$one" horizontalAlignment="stretch">
          <Modal.Title>
            <VisuallyHidden>
              {formatMessage({ id: 'NAVIGATION_MENU' })}
            </VisuallyHidden>
          </Modal.Title>
          <StackLayout as="ul" spacing="$twoAndHalf">
            {navLinks.map(
              ({
                type: linkType,
                message_id: messageId,
                to,
                ...restLinkData
              }) => {
                if (linkType === LinkType.SUB_MENU) {
                  const { subMenu } = restLinkData;
                  return (
                    <StackLayout as="li" spacing="$one" key={messageId}>
                      <MobileNavLandingPageLink
                        messageId={messageId}
                        to={to}
                        onClick={closeMenu}
                      />
                      <StackLayout as="ul" spacing="$one">
                        {subMenu?.items.map((subMenuItem: NavLink) => (
                          <li key={subMenuItem.message_id}>
                            <MobileNavLink
                              text={
                                subMenuItem?.text ||
                                formatMessage({
                                  id: subMenuItem.message_id,
                                })
                              }
                              to={subMenuItem.to}
                              closeMenu={closeMenu}
                            />
                          </li>
                        ))}
                      </StackLayout>
                    </StackLayout>
                  );
                }
                // Handles non-submenu links from useNavLinksList hook
                return (
                  <li key={messageId}>
                    <MobileNavLandingPageLink
                      messageId={messageId}
                      to={to}
                      onClick={closeMenu}
                    />
                  </li>
                );
              },
            )}
            <StackLayout spacing="$one">
              <MobileNavLandingPageLink messageId="USER_SETTINGS" />
              <StackLayout as="ul" spacing="$one">
                {userDropdownConfig.map((item: DropdownSection) =>
                  item.map((configItem: DropdownSection[number]) => (
                    <li key={configItem.text}>
                      <MobileNavLink
                        key={configItem.text}
                        text={configItem.text}
                        to={configItem.to as string}
                        description={configItem?.description}
                        linkProps={configItem?.linkProps}
                        onClick={configItem.onClick}
                        closeMenu={closeMenu}
                      />
                    </li>
                  )),
                )}
              </StackLayout>
            </StackLayout>
          </StackLayout>
        </StackLayout>
      </Modal.Content>
    </Modal.Root>
  );
};
